import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  Bell,
  CheckCheck,
  Headphones,
  Package,
  Radio,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Ticket,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { IconButton } from '@/components/ui';
import { useNotifications } from '@/hooks/useNotifications';
import type { Notification, NotificationType } from '@/types/database';

const ICONS: Record<NotificationType, LucideIcon> = {
  announcement: Sparkles,
  stream_starting: Radio,
  listening_room_opening: Headphones,
  presale_open: Ticket,
  post_reply: Bell,
  mention: Bell,
  order_update: Package,
  moderation_action: ShieldCheck,
  marketplace_approved: ShoppingBag,
  marketplace_rejected: ShoppingBag,
};

function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const {
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    remove,
  } = useNotifications();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  // Dismiss on outside click / escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onRowClick = async (n: Notification) => {
    if (!n.is_read) await markRead(n.id);
    if (n.link_path) {
      setOpen(false);
      navigate(n.link_path);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <IconButton
        label={
          unreadCount > 0
            ? t('notifications.unreadLabel', { count: unreadCount })
            : t('notifications.title')
        }
        size={36}
        variant="flat"
        dot={unreadCount > 0}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="size-[18px]" aria-hidden />
      </IconButton>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 top-[calc(100%+8px)] z-30 flex max-h-[70vh] w-[360px] flex-col overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-surface-2 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)]"
        >
          <header className="flex items-center justify-between border-b border-[color:var(--color-border)] px-4 py-3">
            <p className="text-sm font-semibold text-on-surface">
              {t('notifications.title')}
              {unreadCount > 0 && (
                <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-[color:var(--color-primary-ink)]">
                  {unreadCount}
                </span>
              )}
            </p>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.14em] text-on-surface-muted transition hover:text-primary"
              >
                <CheckCheck className="size-3.5" aria-hidden />
                {t('notifications.markAllRead')}
              </button>
            )}
          </header>

          <div className="flex-1 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <p className="px-4 py-6 text-sm text-on-surface-muted">
                {t('notifications.loading')}
              </p>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
                <Bell className="size-6 text-primary" aria-hidden />
                <p className="font-display text-sm font-medium text-on-surface">
                  {t('notifications.allCaughtUp')}
                </p>
                <p className="text-xs text-on-surface-muted">
                  {t('notifications.allCaughtUpBody')}
                </p>
              </div>
            ) : (
              <ul className="flex flex-col">
                {notifications.map((n) => (
                  <li key={n.id}>
                    <NotificationRow
                      notification={n}
                      onClick={() => void onRowClick(n)}
                      onDismiss={() => void remove(n.id)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationRow({
  notification,
  onClick,
  onDismiss,
}: {
  notification: Notification;
  onClick: () => void;
  onDismiss: () => void;
}) {
  const Icon = ICONS[notification.type] ?? Bell;
  const when = formatDistanceToNowStrict(new Date(notification.created_at), {
    addSuffix: true,
  });
  return (
    <div
      className={[
        'group relative flex gap-3 border-b border-[color:var(--color-border)] px-4 py-3 last:border-b-0',
        notification.is_read ? '' : 'bg-[color:var(--color-primary-dim)]/40',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex flex-1 items-start gap-3 text-left"
      >
        <span
          className={[
            'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
            notification.is_read
              ? 'bg-surface-3 text-on-surface-dim'
              : 'bg-primary text-[color:var(--color-primary-ink)]',
          ].join(' ')}
        >
          <Icon className="size-4" aria-hidden />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="truncate text-[13px] font-semibold text-on-surface">
            {notification.title}
          </p>
          {notification.body && (
            <p className="line-clamp-2 text-[12px] text-on-surface-dim">
              {notification.body}
            </p>
          )}
          <p className="text-[11px] text-on-surface-muted">{when}</p>
        </div>
      </button>
      <DismissButton onDismiss={onDismiss} />
    </div>
  );
}

function DismissButton({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onDismiss}
      aria-label={t('notifications.dismiss')}
      className="absolute right-2 top-2 flex size-6 items-center justify-center rounded-full text-on-surface-muted opacity-0 transition hover:bg-surface-3 hover:text-on-surface group-hover:opacity-100 focus:opacity-100"
    >
      <X className="size-3" aria-hidden />
    </button>
  );
}

export default NotificationBell;
